import { useState } from 'react';
import { changePassword } from '../db';

export default function ForceChangePasswordPage({ user, onPasswordChanged }) {
  const [form, setForm]       = useState({ password: '', password_confirmation: '' });
  const [showNew, setShowNew] = useState(false);
  const [showCon, setShowCon] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState(false);

  const strength = (() => {
    const p = form.password;
    let s = 0;
    if (p.length >= 8)           s++;
    if (/[A-Z]/.test(p))         s++;
    if (/[0-9]/.test(p))         s++;
    if (/[^A-Za-z0-9]/.test(p))  s++;
    return s; // 0–4
  })();

  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'][strength];
  const strengthColor = ['', '#ef4444', '#f59e0b', '#3b82f6', '#22c55e'][strength];
  const mismatch = form.password_confirmation.length > 0 && form.password !== form.password_confirmation;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8)          { setError('Password must be at least 8 characters.'); return; }
    if (form.password !== form.password_confirmation) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const data = await changePassword(form.password, form.password_confirmation);
      // Use server-returned user so must_change_password is definitely false
      const serverUser = data.user || { ...user, must_change_password: false };
      localStorage.setItem('user', JSON.stringify(serverUser));
      setSuccess(true);
      setTimeout(() => onPasswordChanged(serverUser), 1600);
    } catch (err) {
      console.error(err);
      setError('Could not update your password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F2F4F7',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily: "'Montserrat', 'Segoe UI', sans-serif",
    }}>
      <div style={{
        width: '100%', maxWidth: 440,
        background: '#fff',
        borderRadius: 20,
        boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
        overflow: 'hidden',
        animation: 'slideUpPanel 0.4s cubic-bezier(0.34,1.56,0.64,1)',
      }}>
        {/* Top brand bar */}
        <div style={{
          background: '#F47B20',
          padding: '20px 32px',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <div>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11, fontWeight: 600, margin: 0, textTransform: 'uppercase', letterSpacing: 1 }}>Manna Bridal</p>
            <p style={{ color: '#fff', fontSize: 15, fontWeight: 800, margin: 0 }}>Secure Your Account</p>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '28px 32px 32px' }}>
          <p style={{ color: '#6b7280', fontSize: 13, marginTop: 0, marginBottom: 24, lineHeight: 1.6 }}>
            Hello <strong style={{ color: '#1A1A2E' }}>{user?.name}</strong>. Before accessing your dashboard,
            you must set a personal password. This step happens only once.
          </p>

          {success ? (
            <div style={{ textAlign: 'center', padding: '24px 0', animation: 'slideUpPanel 0.3s ease' }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: '#f0fdf4', border: '2px solid #22c55e',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 12px',
              }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <p style={{ color: '#15803d', fontWeight: 700, fontSize: 15, margin: 0 }}>Password updated!</p>
              <p style={{ color: '#9ca3af', fontSize: 13, marginTop: 4 }}>Taking you to your dashboard…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {/* New password field */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  New Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    placeholder="Minimum 8 characters"
                    autoFocus
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      padding: '13px 44px 13px 14px',
                      border: '1.5px solid #e5e7eb', borderRadius: 10,
                      fontSize: 14, color: '#1A1A2E', outline: 'none',
                      transition: 'border 0.2s, box-shadow 0.2s',
                      background: '#fafafa',
                    }}
                    onFocus={e => { e.target.style.borderColor = '#F47B20'; e.target.style.boxShadow = '0 0 0 3px rgba(244,123,32,0.12)'; e.target.style.background = '#fff'; }}
                    onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; e.target.style.background = '#fafafa'; }}
                  />
                  <button type="button" onClick={() => setShowNew(v => !v)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0 }}>
                    {showNew
                      ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    }
                  </button>
                </div>

                {/* Strength bar */}
                {form.password.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 3 }}>
                      {[1,2,3,4].map(i => (
                        <div key={i} style={{
                          flex: 1, height: 3, borderRadius: 99,
                          background: i <= strength ? strengthColor : '#e5e7eb',
                          transition: 'background 0.25s',
                        }} />
                      ))}
                    </div>
                    <span style={{ fontSize: 11, color: strengthColor, fontWeight: 700 }}>{strengthLabel}</span>
                  </div>
                )}
              </div>

              {/* Confirm password field */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Confirm Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showCon ? 'text' : 'password'}
                    value={form.password_confirmation}
                    onChange={e => setForm(f => ({ ...f, password_confirmation: e.target.value }))}
                    placeholder="Re-enter your new password"
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      padding: '13px 44px 13px 14px',
                      border: `1.5px solid ${mismatch ? '#ef4444' : '#e5e7eb'}`,
                      borderRadius: 10, fontSize: 14, color: '#1A1A2E', outline: 'none',
                      background: '#fafafa', transition: 'border 0.2s, box-shadow 0.2s',
                    }}
                    onFocus={e => { e.target.style.boxShadow = '0 0 0 3px rgba(244,123,32,0.12)'; e.target.style.background = '#fff'; }}
                    onBlur={e => { e.target.style.boxShadow = 'none'; e.target.style.background = '#fafafa'; }}
                  />
                  <button type="button" onClick={() => setShowCon(v => !v)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0 }}>
                    {showCon
                      ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                      : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    }
                  </button>
                </div>
                {mismatch && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 4, marginBottom: 0 }}>Passwords do not match</p>}
              </div>

              {error && (
                <div style={{
                  background: '#fef2f2', border: '1px solid #fecaca',
                  borderRadius: 10, padding: '10px 14px', marginBottom: 16,
                  color: '#dc2626', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || strength < 2 || mismatch}
                className="ripple-btn"
                style={{
                  width: '100%', padding: '14px',
                  background: '#F47B20',
                  border: 'none', borderRadius: 12,
                  color: '#fff', fontSize: 14, fontWeight: 700,
                  cursor: (loading || strength < 2 || mismatch) ? 'not-allowed' : 'pointer',
                  opacity: (strength < 2 || mismatch) && !loading ? 0.55 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  boxShadow: '0 4px 14px rgba(244,123,32,0.3)',
                  transition: 'all 0.2s',
                }}
              >
                {loading ? (
                  <>
                    <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff', borderRadius: '50%', animation: 'globalSpin 0.7s linear infinite' }} />
                    Updating password…
                  </>
                ) : 'Set Password & Continue →'}
              </button>

              <p style={{ color: '#9ca3af', fontSize: 11, textAlign: 'center', marginTop: 14, marginBottom: 0 }}>
                🔒 Required once. You won't see this again after completion.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
